import clsx from 'clsx'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import {toast} from 'react-toastify'
import {
  CONTENT_PADDING,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  OUTER_HEIGHT_SETTING,
  TABLE_KEY_COLUMN,
  TABLET_BREAKPOINT,
} from '../../../../../../_gori/constants'
import {setLoadingActionTable, useDisabled} from '../../../../../../_gori/helpers'
import {TableSkeleton} from '../../../../../../_gori/helpers/components'
import useCancelToken from '../../../../../../_gori/hooks/UseCancelToken'
import {
  ConfirmActionSwal,
  DropdownButton,
  Table,
  TableBody,
  TableWrapper,
} from '../../../../../../_gori/partials/widgets'
import {useAuth} from '../../../../auth'
import SettingsService from '../../../core/_requests'
import {CreatePackagesModal} from '../../../modals/shipping/packages/CreatePackagesModal'
import {EditPackagesModal} from '../../../modals/shipping/packages/EditPackagesModal'
import {PackagesFilter} from './PackagesFilter'
import {size} from 'lodash'
import useWindowDimensions from '../../../../../../_gori/hooks/useWindowDimensions'
import {UseTopAlert} from '../../../../../../_gori/hooks/UseTopAlert'

const PackagesPage: React.FC = () => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {loadingSwitch} = useAuth()
  const {setPageDisabled} = useDisabled()
  const [searchParams] = useSearchParams()
  const [loadingFirst, setLoadingFirst] = useState<Boolean>(true)
  const [tableData, setTableData] = useState<any>([])
  const [pagination, setPagination] = useState<any>({})
  const [showFormEdit, setShowFormEdit] = useState<boolean>(false)
  const [packageModel, setPackageModel] = useState<number>()
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
  const [deletePackageId, setDeletePackageId] = useState<number>()
  const [selectedListId, setSelectedListId] = useState<any>([])
  const [showAddPackageModal, setShowAddPackageModal] = useState(false)
  const {width} = useWindowDimensions()
  const {topAlertHeight} = UseTopAlert()

  const setTable = useCallback(async () => {
    const config = {
      params: {
        page: searchParams.get('page') || DEFAULT_PAGE,
        page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
      },
    }
    setPageDisabled(true)
    try {
      const res = await SettingsService.getPackages(config)
      let newPackages = Array.from(res.packages, (packages: any) => {
        return {
          ...packages,
          canCheck: true,
        }
      })
      setTableData(newPackages)
      setPagination(res.pagination)
    } catch (error) {
      if (isCancel(error)) return
      console.error(error)
    } finally {
      setPageDisabled(false)
      setLoadingFirst(false)
    }
  }, [isCancel, searchParams, setPageDisabled])

  const reloadTable = useCallback(() => {
    setTable()
  }, [setTable])

  const handleDeletePackage = async () => {
    if (deletePackageId) {
      setTableData(setLoadingActionTable(tableData, [deletePackageId], true))
      try {
        const response = await SettingsService.deletePackage(
          {package_ids: [deletePackageId]},
          {cancelToken: newCancelToken()}
        )
        if (response) {
          toast.success(intl.formatMessage({id: 'DELETE_SUCCESSFULLY'}))
          reloadTable()
        }
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setTableData(setLoadingActionTable(tableData, [deletePackageId], false))
      }
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: intl.formatMessage({id: 'ID'}),
        accessor: 'canCheck',
      },
      {
        Header: intl.formatMessage({id: 'NAME'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-dark',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='text-hover-primary cursor-pointer text-truncate'>
              {row.original?.name}
            </div>
          )
        },
      },
      {
        Header: intl.formatMessage({id: 'LENGTH'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='text-hover-primary cursor-pointer text-truncate'>
              {Number.parseFloat(row.original?.length)}
            </div>
          )
        },
      },
      {
        Header: intl.formatMessage({id: 'WIDTH'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='text-hover-primary cursor-pointer text-truncate'>
              {Number.parseFloat(row.original?.width)}
            </div>
          )
        },
      },
      {
        Header: intl.formatMessage({id: 'HEIGHT'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='text-hover-primary cursor-pointer text-truncate'>
              {Number.parseFloat(row.original?.height)}
            </div>
          )
        },
      },
      {
        Header: intl.formatMessage({id: 'UNIT'}),
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='text-hover-primary cursor-pointer text-truncate'>
              {row.original.unit}
            </div>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.SUB_ACTIONS,
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => {
          let list: any = [
            {
              label: intl.formatMessage({id: 'EDIT'}),
              action: () => {
                setPackageModel(row.original)
                setShowFormEdit(true)
              },
              className: `cursor-pointer`,
            },
            {
              label: intl.formatMessage({id: 'DELETE'}),
              action: () => {
                setDeletePackageId(row.original.id)
                setShowConfirmModal(true)
              },
              className: `cursor-pointer`,
            },
          ]

          return (
            <DropdownButton
              loading={row.original.isLoading}
              className={clsx({'pe-none': row.original.isLoading})}
              list={list}
            />
          )
        },
      },
    ],
    [intl]
  )

  useEffect(() => {
    if (loadingSwitch) return

    setTable()

    return () => {}
  }, [searchParams, loadingSwitch, setTable])

  return (
    <>
      {showConfirmModal && (
        <ConfirmActionSwal
          show={showConfirmModal}
          title={intl.formatMessage({id: 'DELETE_CUSTOM_PACKAGE'})}
          message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
          messageCancel={intl.formatMessage({id: 'NO'})}
          handleCallBack={handleDeletePackage}
          handleClose={() => setShowConfirmModal(false)}
        />
      )}
      {showFormEdit && (
        <EditPackagesModal
          show={showFormEdit}
          handleClose={() => {
            setShowFormEdit(false)
            setPackageModel(undefined)
          }}
          packageModel={packageModel}
          reloadTable={reloadTable}
        />
      )}
      <CreatePackagesModal
        show={showAddPackageModal}
        handleClose={() => setShowAddPackageModal(false)}
        handleCreatePackagesSuccess={reloadTable}
      />
      <div className='d-flex justify-content-between align-items-center w-100 table-settings-header'>
        <h1 className='d-flex flex-column text-dark fw-boldest my-0 fs-1 section-title'>
          {intl.formatMessage({id: 'PACKAGES'})}
        </h1>
        <div
          className={clsx('btn btn-advanced-primary fs-5', {
            'w-100': width < TABLET_BREAKPOINT,
          })}
          onClick={() => setShowAddPackageModal(true)}
        >
          {`+ ${intl.formatMessage({id: 'ADD_CUSTOM_PACKAGE'})}`}
        </div>
      </div>
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {loadingFirst ? (
          <>
            <TableWrapper>
              <div className='table-content--inset-inner h-100 pt-3'>
                <TableBody>
                  <div className='mt-6 d-flex  align-item-center justify-content-between'>
                    <div className='col-3'>
                      <span className='fs-1 col-12 placeholder placeholder-lg rounded-2 bg-secondary me-2' />
                    </div>
                    <div className='col-2 d-flex flex-row-reverse'>
                      <span className='btn col-5 placeholder placeholder-lg rounded-2 bg-secondary' />
                    </div>
                  </div>
                  <hr className='mb-8' />
                  <TableSkeleton countRow={8} />
                </TableBody>
              </div>
            </TableWrapper>
          </>
        ) : (
          <TableWrapper>
            <div className='table-content--inset-inner h-100 pt-3'>
              <TableBody
                className='mb-6'
                height={`calc(100vh - ${
                  OUTER_HEIGHT_SETTING + CONTENT_PADDING + topAlertHeight
                }px)`}
              >
                {size(selectedListId) > 0 && (
                  <PackagesFilter selectedListId={selectedListId} reloadTable={reloadTable} />
                )}
                <Table
                  columns={columns}
                  data={tableData}
                  pagination={pagination}
                  tbodyClass='text-gray-600 fw-bold'
                  usePagination
                  useCheckBox
                  callBackSetData={(selected) => {
                    setSelectedListId(selected)
                  }}
                />
              </TableBody>
            </div>
          </TableWrapper>
        )}
      </CSSTransition>
    </>
  )
}

export {PackagesPage}
